import React, { useState, useMemo } from "react";
import {
  Input,
  InputGroup,
  Button,
  ListGroup,
  ListGroupItem,
  Badge,
} from "reactstrap";
import { toast } from "react-toastify";

const ConversationalQuestion = ({
  addon,
  userInput,
  setUserInput,
  onSelect,
  onNext,
  onBack,
  showBack,
  inputRef,
  selectedOptions,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const filteredOptions = useMemo(() => {
    if (!userInput.trim()) return addon.options;
    return addon.options.filter((opt) =>
      opt.option.toLowerCase().includes(userInput.toLowerCase()),
    );
  }, [userInput, addon.options]);

  const handleSelectOption = (option) => {
    onSelect(addon, option);
    setUserInput("");
    setShowDropdown(false);
    if (!addon.is_multiple) {
      setTimeout(() => onNext(), 100);
    }
  };

  const handleSubmit = () => {
    if (!userInput.trim()) {
      if (addon.is_multiple) {
        onNext();
      }
      return;
    }

    const matchedOption = filteredOptions[0];
    if (matchedOption) {
      handleSelectOption(matchedOption);
    } else {
      toast.error(`"${userInput}" not found. Please try again.`);
    }
  };

  const currentSelections = selectedOptions[addon.id] || {};
  const selectedOptionsList = Object.keys(currentSelections)
    .filter((optId) => currentSelections[optId])
    .map((optId) => addon.options.find((o) => o.id === optId))
    .filter(Boolean);

  return (
    <div className="conversational-addon-container">
      <div className="mb-4 text-center">
        <h3 style={{ color: "#2c3e50", fontWeight: "600" }}>
          {addon.is_multiple
            ? `Which ${addon.title.toLowerCase()} would you like?`
            : `What ${addon.title.toLowerCase()} would you like?`}
          {addon.is_required && (
            <span style={{ color: "red", marginLeft: "4px" }}>*</span>
          )}
        </h3>
        <p className="text-muted">
          Type to search or click an option
          {addon.is_multiple && <span> • Press Ctrl+Space to continue</span>}
        </p>
      </div>

      {selectedOptionsList.length > 0 && (
        <div className="mb-3">
          <p className="text-muted mb-2">Selected:</p>
          <div className="d-flex flex-wrap gap-2">
            {selectedOptionsList.map((option) => (
              <Badge key={option.id} color="success" pill>
                {option.option}
                {option.price && ` (+$${parseFloat(option.price).toFixed(2)})`}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <div className="mb-3" style={{ position: "relative" }}>
        <InputGroup size="lg">
          <Input
            innerRef={inputRef}
            type="text"
            placeholder="Type to search..."
            value={userInput}
            onChange={(e) => {
              setUserInput(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSubmit();
              } else if (e.ctrlKey && e.key === " ") {
                e.preventDefault();
                if (addon.is_multiple) {
                  onNext();
                }
              }
            }}
          />
          <Button color="primary" onClick={handleSubmit}>
            {addon.is_multiple ? "Next" : "Select"}
          </Button>
        </InputGroup>

        {showDropdown && filteredOptions.length > 0 && (
          <ListGroup
            style={{
              position: "absolute",
              zIndex: 1000,
              width: "100%",
              maxHeight: "300px",
              overflowY: "auto",
            }}
          >
            {filteredOptions.map((option) => (
              <ListGroupItem
                key={option.id}
                action
                onClick={() => handleSelectOption(option)}
                style={{ cursor: "pointer" }}
              >
                {option.option}
                {option.price && (
                  <span className="text-muted">
                    {" "}
                    (+${parseFloat(option.price).toFixed(2)})
                  </span>
                )}
              </ListGroupItem>
            ))}
          </ListGroup>
        )}
      </div>

      <div className="available-options">
        <p className="text-muted mb-2">All options:</p>
        <div className="d-flex flex-wrap gap-2">
          {addon.options.map((option) => (
            <Button
              key={option.id}
              size="sm"
              color={currentSelections[option.id] ? "success" : "dark"}
              outline={!currentSelections[option.id]}
              onClick={() => handleSelectOption(option)}
            >
              {option.option}
              {option.price && ` (+$${parseFloat(option.price).toFixed(2)})`}
            </Button>
          ))}
        </div>
      </div>

      {showBack && (
        <div className="mt-3">
          <Button size="sm" color="secondary" outline onClick={onBack}>
            ← Back
          </Button>
        </div>
      )}
    </div>
  );
};

export default ConversationalQuestion;
